/**
 * Turning a raw user-agent string into something a person recognises.
 *
 * Lives on the client, not the server: this is presentation, so improving it
 * needs no API deploy, and the server keeps storing the unabridged string
 * either way. Extracted into its own module purely so it can be unit-tested —
 * it is the one piece of the security screen with branching logic, and the
 * device list is where someone decides whether a session is theirs.
 */

//Order matters in both tables. Every Chromium browser also claims "Safari" and
//most claim "Chrome", so the more specific brands have to be tested first or
//Edge and Opera both come out as "Chrome".
const BROWSERS = [
  [/Edg\//, 'Edge'],
  [/OPR\/|Opera/, 'Opera'],
  [/SamsungBrowser\//, 'Samsung Internet'],
  [/Firefox\/|FxiOS\//, 'Firefox'],
  [/CriOS\//, 'Chrome'],
  [/Chrome\//, 'Chrome'],
  [/Safari\//, 'Safari'],
];

//iPadOS 13+ reports a desktop Macintosh UA, so "Mac" has to be checked last
//and even then an iPad may read as a Mac. That is a known limit of UA sniffing
//and not worth a fingerprinting library to fix for a label.
const PLATFORMS = [
  [/iPhone/, 'iPhone'],
  [/iPad/, 'iPad'],
  [/Android/, 'Android'],
  [/Windows/, 'Windows'],
  [/CrOS/, 'ChromeOS'],
  [/Mac OS X|Macintosh/, 'Mac'],
  [/Linux/, 'Linux'],
];

const firstMatch = (table, ua, fallback) => {
  for (const [re, name] of table) if (re.test(ua)) return name;
  return fallback;
};

export function describeDevice(ua) {
  //A session written before UserAgent was captured, or a client that sent
  //none. "Unknown device" is honest; guessing would be worse on the one
  //screen where the user is deciding what to sign out.
  if (!ua) return 'Unknown device';
  const browser = firstMatch(BROWSERS, ua, 'Browser');
  const platform = firstMatch(PLATFORMS, ua, null);
  return platform ? `${browser} on ${platform}` : browser;
}

/** Coarse relative time. Precision past "today" is noise on this screen. */
export function formatWhen(ms) {
  if (!ms) return null;
  const diff = Date.now() - ms;
  //Negative means clock skew between the server (which stamped it) and this
  //browser. "just now" beats "in 3 minutes", which reads as a bug.
  if (diff < 60_000) return 'just now';
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)} min ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)} h ago`;
  return new Date(ms).toLocaleDateString();
}

/**
 * A stored timestamp as "when did this happen", for a table column.
 *
 * Wraps formatWhen for the two cases a DATETIME column has and a millisecond
 * number does not:
 *
 *   null  → "Never". The honest answer for a user who has not signed in since
 *           sign-ins started being recorded, and NOT the same statement as the
 *           "Invite pending" badge beside it — somebody can have accepted their
 *           invitation long ago and simply not been back.
 *   junk  → an em dash. A column that prints "Invalid Date" looks like a fault in
 *           the app rather than in one row.
 *
 * Shared by the "Last signed in" column in Settings and the "When" column in
 * Login activity, because they are the same question about the same stored value
 * and two copies would drift on the empty case first.
 */
export function formatLastSeen(value, { never = 'Never' } = {}) {
  if (value === null || value === undefined || value === '') return never;
  const ms = value instanceof Date ? value.getTime() : new Date(value).getTime();
  if (Number.isNaN(ms)) return '\u2014';
  return formatWhen(ms) || never;
}
